<template>
  <div class="estimation-documents" v-if="productBundleExtraction">
    <div>
      <div class="estimation-documents__card">
        <div class="estimation-documents__header">
          <div>
            <div class="estimation__header--title">{{ $t('estimation.estimation-documents.documents') }}</div>
            <div class="estimation__header--subtitle">
              {{ $t('estimation.estimation-documents.extraction-consultation') }}
            </div>
          </div>
          <div class="estimation-documents__header--actions">
            <!-- Display extraction parameters -->
            <div class="evaluation-grid__edit-btn" @click="openEstimationParameters">
              <BaseIcon name="eye" />{{ $t('estimation.estimation-documents.parameters') }}
            </div>
            <BaseRoundButton
              v-if="!isExtractionPending"
              icon="refresh-cw"
              is-white
              :size="40"
              class="estimation-documents__reset-button"
              :tooltip-text="$t('estimation.estimation-documents.reset-extraction')"
              @click.native="initiateEstimationProcessReset"
            />
          </div>
        </div>
        <!-- Extraction is pending -->
        <div v-if="isExtractionPending" class="estimation-documents__pending-box">
          <BaseCircularLoadingBar :size="40" />
          <div class="estimation-documents__pending-text">
            <div class="estimation-documents__pending-title">
              {{ $t('estimation.estimation-documents.extraction-pending') }}
            </div>
            <div class="estimation-documents__pending-subtitle">
              {{ $t('estimation.estimation-documents.user-wait-notification') }}
            </div>
          </div>
        </div>
        <!-- Else, show the extraction's result -->
        <div v-else class="estimation-documents__content">
          <div class="estimation-documents__documents-block">
            <h3 class="title">{{ $t('estimation.estimation-documents.AI-documents') }}</h3>
            <div class="estimation-documents__manual-documents">
              <div v-if="!productBundleGeneratedFiles.length">
                <div class="estimation-documents__file-extraction">
                  <div class="estimation-documents__file-informations">
                    <h2 class="title">{{ $t('estimation.estimation-documents.empty-documents') }}</h2>
                  </div>
                </div>
              </div>
              <div v-for="(file, index) in productBundleGeneratedFiles" :key="index" class="document-button">
                <div class="estimation-documents__file-extraction">
                  <div>
                    <BaseIcon name="file-excel" class="estimation-documents__icon" />
                  </div>
                  <div class="estimation-documents__file-informations">
                    <h2 class="title">{{ file.name }}</h2>
                    <span> {{ $t('estimation.estimation-documents.master') }} </span>
                  </div>
                  <BaseRoundButton
                    @click.native="downloadFile(file)"
                    :size="40"
                    :tooltip-text="$t('actions.download')"
                    icon="download"
                    is-transparent
                  />
                </div>
              </div>
            </div>
          </div>
          <span class="estimation-documents__divider"></span>
          <div class="estimation-documents__documents-block">
            <h3 class="title">{{ $t('estimation.estimation-documents.manual-documents') }}</h3>
            <div class="estimation-documents__manual-documents">
              <div v-for="(file, index) in productBundleManualFiles" :key="index" class="document-button">
                <!-- TODO: En faire un component responsive -->
                <div class="estimation-documents__file-extraction">
                  <div>
                    <BaseIcon :name="getDocumentIcon(file)" class="estimation-documents__icon" />
                  </div>
                  <div class="estimation-documents__file-informations">
                    <h2 class="title">{{ file.name }}</h2>
                    <span> {{ $t('estimation.estimation-documents.added-manually') }} </span>
                  </div>
                  <BaseRoundButton
                    @click.native="downloadFile(file)"
                    :size="40"
                    :tooltip-text="$t('actions.download')"
                    icon="download"
                    is-transparent
                  />
                  <!-- Phones: in a sheet, as a row's ⋯ (docs/page-layout.md). -->
                  <BaseMoreActions
                    v-if="isPhone"
                    :title="file.name"
                    :actions="deleteActions"
                    @select="deleteFile(file)"
                  />
                  <v-menu v-else v-model="actionMenu" absolute>
                    <template v-slot:activator="{ on, attrs }">
                      <div class="document-tag__action" @click.stop v-on="on" v-bind="attrs">
                        <BaseIcon class="document-tag__action-icon" name="more-horizontal" />
                      </div>
                    </template>
                    <div class="base-actions-menu">
                      <ul class="base-actions-menu__ul">
                        <li class="base-actions-menu__li delete" @click="deleteFile(file)">
                          <BaseIcon :size="44" name="trash-2" />
                          {{ $t('actions.delete') }}
                        </li>
                      </ul>
                    </div>
                  </v-menu>
                </div>
              </div>
              <div class="estimation-documents__documents-block">
                <div v-if="pending">
                  <BaseCircularLoadingBar :size="50" :text="$t('page.opportunity.loading-document-text')" />
                </div>
                <div v-else>
                  <FileUpload
                    minimalist
                    multiple
                    v-if="productBundleManualFiles.length"
                    @add-files="uploadFile($event, file)"
                  />
                  <FileUpload v-else estimation multiple @add-files="uploadFile" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <EstimationParametersDialog ref="estimationParametersDialog" />
    <ConfirmationDialog
      ref="confirmationDialogResetExtraction"
      :buttonText="$t('actions.reset')"
      @execute="executeInitiateResetExtraction"
    >
      <template slot="content">
        <div class="confirmation-dialog__content">
          <i18n path="estimation.estimation-documents.reset-extraction-confirmation">
            <template v-slot:action>
              <span class="confirmation-dialog__action"> {{ $t('actions.reset') }} </span>
            </template>
          </i18n>
        </div>
      </template>
    </ConfirmationDialog>
    <ConfirmationDialog
      ref="confirmationDialogDeleteOutputFile"
      :buttonText="$t('actions.confirm')"
      @execute="executeDeleteFile(manipulatedFile)"
    >
      <template slot="content">
        <div class="confirmation-dialog__content">
          <i18n path="estimation.estimation-documents.delete-output-file-confirmation">
            <template v-slot:file v-if="manipulatedFile">
              <span class="confirmation-dialog__action">{{ manipulatedFile.name }}</span>
            </template>
          </i18n>
        </div>
      </template>
    </ConfirmationDialog>
  </div>
</template>

<script>
import BaseCircularLoadingBar from '@/components/base/BaseCircularLoadingBar';
import BaseIcon from '@/components/base/BaseIcon';
import BaseMoreActions from '@/components/base/BaseMoreActions.vue';
import BaseRoundButton from '@/components/base/BaseRoundButton';
import BaseButton from '@/components/base/BaseButton';
import EstimationParametersDialog from '@/components/page-opportunity-estimation/EstimationParametersDialog';
import FileUpload from '@/components/form/FileUpload';
import ConfirmationDialog from '@/components/page-admin-invites/ConfirmationDialog';

import { mapActions, mapGetters } from 'vuex';
import { viewport } from '@/util/viewport';
import { getDocumentIcon } from '@/util/document-file-util';

export default {
  name: 'EstimationDocuments',
  components: {
    BaseCircularLoadingBar,
    BaseIcon,
    BaseMoreActions,
    BaseRoundButton,
    FileUpload,
    EstimationParametersDialog,
    ConfirmationDialog,
  },
  props: {
    opportunityId: String,
  },
  data() {
    return {
      manipulatedFile: null,
    };
  },
  computed: {
    isPhone() {
      return viewport.size === 'phone';
    },
    deleteActions() {
      return [{ key: 'delete', label: this.$t('actions.delete'), icon: 'trash-2', danger: true }];
    },
    ...mapGetters('productBundleExtraction', ['productBundleExtraction', 'productBundleExtractionRunning']),
    ...mapGetters('productBundleExtractionOutputFiles', ['pending']),
    isExtractionPending() {
      return this.productBundleExtractionRunning;
    },
    productBundleGeneratedFiles() {
      return this.productBundleExtraction.outputFiles.filter((file) => file.generated);
    },
    productBundleManualFiles() {
      return this.productBundleExtraction.outputFiles.filter((file) => !file.generated);
    },
  },
  methods: {
    ...mapActions('productBundleExtractionOutputFiles', [
      'downloadProductBundleExtractionOutputFile',
      'uploadProductBundleExtractionOutputFile',
      'deleteProductBundleExtractionOutputFileById',
    ]),
    ...mapActions('productBundleExtractions', ['initiateResetProductBundleExtraction']),
    getDocumentIcon,
    downloadFile(file) {
      const { productBundleExtractionId, id, name } = file;
      this.downloadProductBundleExtractionOutputFile({ bundleId: productBundleExtractionId, fileId: id, name: name });
    },
    uploadFile(file) {
      const { id } = this.productBundleExtraction;
      this.uploadProductBundleExtractionOutputFile({ bundleId: id, files: [file] });
    },
    openEstimationParameters() {
      this.$refs.estimationParametersDialog.openDialog();
    },
    initiateEstimationProcessReset() {
      this.$refs.confirmationDialogResetExtraction.openDialog();
    },
    executeInitiateResetExtraction() {
      const { id } = this.productBundleExtraction;

      if (!id) return;
      this.initiateResetProductBundleExtraction({ id });
    },
    deleteFile(file) {
      this.$refs.confirmationDialogDeleteOutputFile.openDialog();
      this.manipulatedFile = file;
    },
    executeDeleteFile(file) {
      const { id } = this.productBundleExtraction;

      this.deleteProductBundleExtractionOutputFileById({ bundleId: id, fileId: file.id })
        .then(() => {
          this.$toast.success(this.$t('estimation.estimation-documents.output-file-delete-success'));
        })
        .catch((error) => {
          this.$toast.error(error);
        });
    },
  },
};
</script>

<style lang="scss">
.estimation-documents {
  height: calc(var(--viewport-height) - 56px);
  width: 100%;

  &__card {
    width: 100%;
    max-width: 850px;
    background-color: $color-white;
    height: auto;
    border-radius: $border-radius-root;
    padding: 16px;
    margin: 2% auto 5%;
    position: relative;
  }

  &__pending-box {
    padding: 64px;
    display: flex;
    flex-direction: column;
    gap: 32px;
  }

  &__pending-text {
    display: flex;
    flex-direction: column;
    gap: 16px;
    justify-items: center;
    text-align: center;
  }

  &__pending-title {
    font-size: 18px;
    font-weight: bold;
  }

  &__pending-subtitle {
    margin-left: auto;
    margin-right: auto;
    display: flex;
    max-width: 400px;
  }

  &__divider {
    background: $color-border;
    display: block;
    height: 1px;
    margin-top: 16px;
    width: 100%;
  }

  &__documents-block {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 20px;
  }

  &__reset {
    display: flex;
    justify-content: center;
    padding: 16px;
    margin: 2% auto 2%;
  }

  &__reset-button {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__header {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 8px;
    justify-content: space-between;

    h2,
    h3 {
      text-align: left;
    }

    &--actions {
      display: flex;
      gap: 4px;
      align-items: center;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    background-color: $color-white;
    width: 100%;
    padding: 8px;
    gap: 20px;
    border-radius: $border-radius-root;
  }

  &__manual-documents {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  h2 {
    font-size: 14px;
    line-height: 14px;
    font-weight: 700;
    color: $color-normal;
  }

  h3 {
    font-size: 16px;
    line-height: 19.2px;
    font-weight: 500;
    color: $color-normal;
  }

  p {
    color: $color-warm-blue;
  }

  &__file-extraction {
    display: flex;
    align-items: center;
    flex-direction: row;
    gap: 16px;
  }

  .document-button {
    color: grey;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    font-size: $font-size-small;
    background-color: $color-slate-100;
    padding: 16px;
    border-radius: $border-radius-root;
    width: fit-content;
    transition: 200ms;
  }

  .document-button h2 {
    font-size: 16px;
    margin-bottom: 8px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .document-button span {
    font-size: 14px;
    line-height: 1.4;
  }

  .reset-icon {
    margin-right: 8px;
  }
}
</style>
